import React from 'react';
import { Link } from 'react-router-dom';
import { Camera, Shield, ArrowRight } from 'lucide-react';

/**
 * Live Streaming & Photography Notice.
 *
 * Reusable calm-professional notice for church entrances, event pages,
 * registration forms and children's programme registrations. Follows the
 * MoJ brand palette (purple/gold/cream) and stays deliberately non-alarming
 * — this is informational, not a warning.
 *
 * Props:
 *   compact: bool — renders a slimmer variant suitable for embedding above a
 *                   form submit button (fewer body paragraphs, no CTA row).
 *   hideActions: bool — hide the "View Privacy Policy" and "Contact Media Team" buttons.
 *   className: extra tailwind classes.
 */
export default function LiveStreamingNotice({
  compact = false,
  hideActions = false,
  className = '',
}) {
  return (
    <aside
      role="note"
      aria-labelledby="livestream-notice-heading"
      data-testid="live-streaming-notice"
      className={`rounded-2xl border-2 border-moj-gold/40 bg-white/80 backdrop-blur-sm p-4 sm:p-5 ${className}`}
    >
      <div className="flex items-start gap-3">
        <div
          aria-hidden
          className="w-10 h-10 rounded-xl gradient-purple flex items-center justify-center flex-shrink-0"
        >
          <Camera size={18} className="text-moj-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            id="livestream-notice-heading"
            className="text-[11px] sm:text-xs uppercase tracking-widest font-bold text-moj-gold-deep m-0"
          >
            Live Streaming &amp; Photography Notice
          </h3>
          <p className="text-sm text-moj-charcoal mt-1.5 leading-relaxed">
            Photography, video recording, and live streaming may take place during selected
            Ministry of Jesus services and events.
          </p>
          {!compact && (
            <>
              <p className="text-sm text-moj-charcoal mt-2 leading-relaxed">
                Recorded or live content may appear on the Ministry of Jesus website,
                YouTube channel, and official social-media platforms. Camera appearance
                concerns may be discussed with the Media Team before the service or event.
              </p>
              <p className="text-sm text-moj-charcoal mt-2 leading-relaxed flex items-start gap-2">
                <Shield
                  size={14}
                  aria-hidden
                  className="text-moj-gold-deep flex-shrink-0 mt-0.5"
                />
                <span>
                  <strong className="text-moj-purple-deep">Children &amp; young people:</strong>{' '}
                  additional safeguarding measures apply. Identifiable children&apos;s
                  photographs or recordings intended for public publication will normally
                  require appropriate parent or legal-guardian consent.
                </span>
              </p>
            </>
          )}

          {!hideActions && (
            <div className="flex flex-wrap gap-2 mt-3">
              <Link
                to="/privacy#photography"
                className="inline-flex items-center gap-1 text-xs sm:text-sm font-semibold text-moj-purple hover:text-moj-purple-deep underline underline-offset-2"
                data-testid="livestream-notice-privacy-link"
              >
                View Privacy Policy <ArrowRight size={12} />
              </Link>
              <span aria-hidden className="text-moj-charcoal/30 hidden sm:inline">·</span>
              <Link
                to="/contact"
                className="inline-flex items-center gap-1 text-xs sm:text-sm font-semibold text-moj-purple hover:text-moj-purple-deep underline underline-offset-2"
                data-testid="livestream-notice-contact-link"
              >
                Contact Media Team
              </Link>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
